import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FileText, Shield, CreditCard, Mail, ChevronUp } from 'lucide-react';

const SectionHeading = ({ children }) => (
  <h2 className="font-heading text-xl md:text-2xl font-bold text-slate-900 mt-10 mb-4">{children}</h2>
);

const SectionText = ({ children }) => (
  <p className="text-slate-600 leading-relaxed mb-3">{children}</p>
);

const BulletList = ({ items }) => (
  <ul className="list-disc list-outside ml-6 mb-4 space-y-1.5">
    {items.map((item, i) => (
      <li key={i} className="text-slate-600 leading-relaxed">{item}</li>
    ))}
  </ul>
);

const TermsPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('terms');
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    if (location.hash === '#refund') setActiveTab('refund');
    else if (location.hash === '#privacy') setActiveTab('privacy');
  }, [location.hash]);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const tabs = [
    { id: 'terms', label: 'Terms & Conditions', icon: FileText },
    { id: 'privacy', label: 'Privacy Policy', icon: Shield },
    { id: 'refund', label: 'Refund Policy', icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 pt-12 pb-6">
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-2">
            Legal
          </h1>
          <p className="text-slate-500 text-base">
            EvalentX platform policies and legal information.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex gap-1 border-b border-slate-200 -mb-px overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#0A66C2] text-[#0A66C2]'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-8 md:p-12">

          {/* ══════════════════════ TERMS & CONDITIONS ══════════════════════ */}
          {activeTab === 'terms' && (
            <div data-testid="terms-content">
              <h1 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 mb-6">
                Terms & Conditions
              </h1>

              <SectionHeading>1. Introduction</SectionHeading>
              <SectionText>
                Welcome to EvalentX. These Terms & Conditions govern access to and use of the EvalentX website, platform, services, events, competitions, assessments, rankings, hiring challenges, and related offerings.
              </SectionText>
              <SectionText>
                By accessing or using EvalentX, you agree to be bound by these Terms & Conditions.
              </SectionText>

              <SectionHeading>2. Platform Services</SectionHeading>
              <SectionText>EvalentX may provide one or more of the following services:</SectionText>
              <BulletList items={[
                'Competitions, hackathons, olympiads, and assessments',
                'Talent rankings and institutional rankings',
                'Hiring challenges and recruitment-related programs',
                'School, college, and institutional engagement programs',
                'Event hosting, event listing, and participation management',
                'Certificates, reports, dashboards, analytics, and digital profiles',
              ]} />

              <SectionHeading>3. User Categories</SectionHeading>
              <SectionText>These Terms apply to all users of the platform, including:</SectionText>
              <BulletList items={[
                'Students and participants',
                'Parents or guardians, where applicable',
                'Schools, colleges, and institutional representatives',
                'Companies, recruiters, and hiring partners',
                'Event hosts, organizers, sponsors, and collaborators',
              ]} />

              <SectionHeading>4. Eligibility</SectionHeading>
              <SectionText>
                Users must provide accurate and complete information while using EvalentX. If a participant is a minor, registration and participation may require consent from a parent, guardian, school, or other authorized representative where applicable.
              </SectionText>

              <SectionHeading>5. Account Registration and User Responsibility</SectionHeading>
              <SectionText>
                Users are responsible for maintaining the confidentiality of their account credentials and for all activity conducted through their account.
              </SectionText>
              <SectionText>Users agree not to:</SectionText>
              <BulletList items={[
                'Impersonate any person or entity',
                'Provide false, misleading, or incomplete information',
                'Create duplicate or fraudulent accounts',
                'Misuse the platform or interfere with its normal operation',
              ]} />
              <SectionText>
                EvalentX reserves the right to suspend or terminate accounts that violate these Terms.
              </SectionText>

              <SectionHeading>6. Competitions, Assessments, Olympiads, and Events</SectionHeading>
              <SectionText>
                Participation in competitions, assessments, olympiads, events, and hackathons is subject to specific rules, eligibility conditions, deadlines, and judging criteria published for each activity.
              </SectionText>
              <SectionText>Participants agree that:</SectionText>
              <BulletList items={[
                'All submissions must be original unless otherwise permitted',
                'Plagiarism, cheating, unfair means, impersonation, or misconduct may result in immediate disqualification',
                'EvalentX may verify identity, eligibility, and authenticity of submissions',
                'The decision of judges, evaluators, or EvalentX shall be final in relation to rankings, results, and awards',
              ]} />

              <SectionHeading>7. Rankings, Scores, Analytics, and Reports</SectionHeading>
              <SectionText>
                EvalentX may generate individual, institutional, subject-wise, or event-wise rankings and analytics based on performance, participation, or other relevant criteria.
              </SectionText>
              <SectionText>By using the platform, users acknowledge that:</SectionText>
              <BulletList items={[
                'Rankings and analytics are based on the data available to EvalentX',
                'Methodologies may evolve over time',
                'Results may be published on the website, dashboards, reports, certificates, or promotional materials unless restricted by law or specific agreement',
              ]} />

              <SectionHeading>8. Hiring, Recruitment, and Opportunity-Related Services</SectionHeading>
              <SectionText>
                EvalentX may facilitate connections between talent and companies through hiring challenges, assessments, applications, or related recruitment activities.
              </SectionText>
              <SectionText>EvalentX does not guarantee:</SectionText>
              <BulletList items={[
                'Selection, interview calls, internships, jobs, admissions, scholarships, or offers',
                'Employer response, candidate response, or final hiring outcome',
                'Continued availability of any listed opportunity',
              ]} />
              <SectionText>
                Companies and institutions are solely responsible for their own selection criteria, communications, interviews, and final decisions.
              </SectionText>

              <SectionHeading>9. Event Hosts, Institutions, and Companies</SectionHeading>
              <SectionText>Schools, colleges, companies, and event hosts using EvalentX agree to:</SectionText>
              <BulletList items={[
                'Provide accurate and lawful information',
                'Avoid misleading claims or false commitments',
                'Comply with all applicable laws and institutional requirements',
                'Ensure that their content, opportunities, and event details are genuine and authorized',
              ]} />
              <SectionText>
                EvalentX may approve, reject, edit, suspend, or remove listings, programs, or hosted events at its discretion.
              </SectionText>

              <SectionHeading>10. Payments and Fees</SectionHeading>
              <SectionText>
                Certain services on EvalentX may require payment, including but not limited to event registrations, assessments, olympiads, premium access, hosting charges, or participation fees.
              </SectionText>
              <SectionText>Users acknowledge that:</SectionText>
              <BulletList items={[
                'All applicable fees will be displayed before payment',
                'Payment once made is subject to the applicable Refund Policy',
                'Payment gateway providers may impose their own terms and technical conditions',
                'Taxes, gateway charges, or convenience fees may apply where relevant',
              ]} />

              <SectionHeading>11. Certificates, Recognition, and Awards</SectionHeading>
              <SectionText>
                Certificates, awards, badges, rankings, and recognitions are issued based on the applicable rules of the program, event, or assessment.
              </SectionText>
              <SectionText>EvalentX reserves the right to withhold, revoke, or cancel any certificate, award, or recognition in cases involving:</SectionText>
              <BulletList items={[
                'Misconduct',
                'Ineligibility',
                'Fraud',
                'Plagiarism',
                'Violation of platform rules',
              ]} />

              <SectionHeading>12. Intellectual Property</SectionHeading>
              <SectionText>
                All platform content, branding, website design, software, text, graphics, logos, dashboards, and proprietary methodologies on EvalentX are protected by applicable intellectual property laws.
              </SectionText>
              <SectionText>
                Users retain ownership of original content submitted by them, but by submitting content to EvalentX, they grant EvalentX a non-exclusive right to use, reproduce, display, publish, and promote such content for evaluation, operations, reporting, and promotional purposes, unless otherwise agreed in writing.
              </SectionText>

              <SectionHeading>13. Privacy and Data Usage</SectionHeading>
              <SectionText>
                Use of EvalentX is also governed by the Privacy Policy. By using the platform, users consent to the collection, use, storage, and sharing of information as described in the Privacy Policy.
              </SectionText>

              <SectionHeading>14. Prohibited Conduct</SectionHeading>
              <SectionText>Users shall not:</SectionText>
              <BulletList items={[
                'Cheat, hack, scrape, or misuse the platform',
                'Upload malicious, unlawful, defamatory, or infringing content',
                'Misuse data of other users or institutions',
                'Attempt to manipulate rankings, submissions, or assessments',
                'Use the platform in violation of any law or regulation',
              ]} />

              <SectionHeading>15. Limitation of Liability</SectionHeading>
              <SectionText>
                To the maximum extent permitted by law, EvalentX shall not be liable for any indirect, incidental, consequential, or special damages arising from:
              </SectionText>
              <BulletList items={[
                'Use of or inability to use the platform',
                'Technical errors or downtime',
                'Decisions made by employers, institutions, judges, or partners',
                'Cancellations, delays, or modifications of events or opportunities',
              ]} />

              <SectionHeading>16. Suspension, Modification, and Termination</SectionHeading>
              <SectionText>
                EvalentX may modify, suspend, or discontinue any part of the platform at any time, with or without notice.
              </SectionText>
              <SectionText>
                EvalentX may also suspend or terminate user access in cases of misconduct, non-compliance, fraud, abuse, or legal necessity.
              </SectionText>

              <SectionHeading>17. Changes to Terms</SectionHeading>
              <SectionText>
                EvalentX may revise these Terms & Conditions from time to time. Continued use of the platform after updates constitutes acceptance of the revised Terms.
              </SectionText>

              <SectionHeading>18. Governing Law and Jurisdiction</SectionHeading>
              <SectionText>
                These Terms shall be governed by the laws of India. Any disputes shall be subject to the jurisdiction of competent courts in India.
              </SectionText>

              <SectionHeading>19. Platform Management</SectionHeading>
              <SectionText>
                EvalentX is managed by Codemania Edu Tech Pvt Ltd.
              </SectionText>

              <SectionHeading>20. Contact</SectionHeading>
              <SectionText>For questions related to these Terms & Conditions, contact:</SectionText>
              <div className="mt-2 inline-flex items-center gap-2 bg-blue-50 rounded-lg px-4 py-2.5">
                <Mail size={16} className="text-[#0A66C2]" />
                <a href="mailto:support@evalentx.com" className="text-[#0A66C2] font-semibold hover:underline">support@evalentx.com</a>
              </div>
            </div>
          )}

          {/* ══════════════════════ PRIVACY POLICY ══════════════════════ */}
          {activeTab === 'privacy' && (
            <div data-testid="privacy-content">
              <h1 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 mb-6">
                Privacy Policy
              </h1>

              <SectionHeading>1. Introduction</SectionHeading>
              <SectionText>
                This Privacy Policy explains how EvalentX collects, uses, stores, processes, and shares information of users who interact with the platform.
              </SectionText>
              <SectionText>
                By using EvalentX, you consent to the practices described in this Privacy Policy.
              </SectionText>

              <SectionHeading>2. Information We Collect</SectionHeading>
              <SectionText>We may collect the following categories of information:</SectionText>

              <h3 className="text-base font-bold text-slate-800 mt-4 mb-2">a. Personal Information</h3>
              <BulletList items={['Name', 'Email address', 'Phone number', 'Date of birth', 'Gender, where applicable', 'Profile photo, where applicable']} />

              <h3 className="text-base font-bold text-slate-800 mt-4 mb-2">b. Academic or Institutional Information</h3>
              <BulletList items={['School or college name', 'Class, semester, course, stream, or department', 'Graduation year or academic status']} />

              <h3 className="text-base font-bold text-slate-800 mt-4 mb-2">c. Participation and Performance Information</h3>
              <BulletList items={['Event registrations', 'Assessment scores', 'Rankings', 'Submissions', 'Certificates', 'Skill profiles', 'Analytics and dashboard activity']} />

              <h3 className="text-base font-bold text-slate-800 mt-4 mb-2">d. Company, Institution, or Host Information</h3>
              <BulletList items={['Organization name', 'Representative name', 'Designation', 'Official contact details', 'Company or institution profile information']} />

              <h3 className="text-base font-bold text-slate-800 mt-4 mb-2">e. Technical and Usage Information</h3>
              <BulletList items={['IP address', 'Browser/device information', 'Session and usage logs', 'Cookies and related tracking information']} />

              <SectionHeading>3. How We Use Information</SectionHeading>
              <SectionText>We may use collected information to:</SectionText>
              <BulletList items={[
                'Provide platform services',
                'Register users for events, tests, and competitions',
                'Evaluate participation and generate results, rankings, reports, and certificates',
                'Facilitate hiring, admissions, scholarship, and opportunity-related workflows',
                'Improve platform performance and user experience',
                'Communicate updates, offers, schedules, reminders, and support messages',
                'Generate institutional, talent, and performance analytics',
                'Prevent misuse, fraud, or violations of platform rules',
              ]} />

              <SectionHeading>4. Sharing of Information</SectionHeading>
              <SectionText>We may share relevant information with:</SectionText>
              <BulletList items={[
                'Schools, colleges, and institutions associated with a program',
                'Companies, recruiters, or hiring partners where participation is linked to opportunities',
                'Event hosts, sponsors, judges, or evaluators where necessary',
                'Service providers such as payment gateway partners, email providers, analytics tools, and technical vendors',
                'Legal or regulatory authorities where required by law',
              ]} />
              <SectionText>
                EvalentX does not sell personal information as a general commercial practice.
              </SectionText>

              <SectionHeading>5. Rankings, Results, and Public Visibility</SectionHeading>
              <SectionText>
                Where relevant to the nature of the program or event, participant names, institutional affiliation, scores, rankings, achievements, certificates, and related performance information may be displayed publicly on leaderboards, reports, event pages, recognition pages, or promotional materials.
              </SectionText>

              <SectionHeading>6. Cookies and Tracking</SectionHeading>
              <SectionText>EvalentX may use cookies and related technologies to:</SectionText>
              <BulletList items={[
                'Keep users logged in',
                'Understand usage patterns',
                'Improve platform performance',
                'Personalize user experience',
              ]} />
              <SectionText>
                Users may manage cookies through browser settings, though some features may not function properly if cookies are disabled.
              </SectionText>

              <SectionHeading>7. Data Security</SectionHeading>
              <SectionText>
                We use reasonable administrative, technical, and organizational safeguards to protect user information. However, no system can guarantee absolute security, and users acknowledge that internet-based platforms involve inherent risks.
              </SectionText>

              <SectionHeading>8. Data Retention</SectionHeading>
              <SectionText>We may retain user information for as long as necessary to:</SectionText>
              <BulletList items={[
                'Provide services',
                'Maintain records',
                'Comply with legal obligations',
                'Preserve rankings, certificates, participation histories, and related analytics',
              ]} />

              <SectionHeading>9. User Rights</SectionHeading>
              <SectionText>Subject to applicable law, users may request to:</SectionText>
              <BulletList items={[
                'Access their information',
                'Correct inaccurate information',
                'Update profile details',
                'Request deletion of certain data',
                'Opt out of non-essential communications',
              ]} />
              <SectionText>
                Certain records may need to be retained for compliance, reporting, or legitimate operational purposes.
              </SectionText>

              <SectionHeading>10. Children and Minors</SectionHeading>
              <SectionText>
                Where users are minors, schools, parents, guardians, or authorized representatives may be involved in consent, participation, and communication processes, as applicable.
              </SectionText>

              <SectionHeading>11. Third-Party Services</SectionHeading>
              <SectionText>
                EvalentX may include integrations with third-party services such as payment processors, analytics tools, video tools, communication providers, or embedded services. Use of such services may also be subject to their own privacy policies and terms.
              </SectionText>

              <SectionHeading>12. Changes to Privacy Policy</SectionHeading>
              <SectionText>
                EvalentX may update this Privacy Policy from time to time. Continued use of the platform after changes are published constitutes acceptance of the updated policy.
              </SectionText>

              <SectionHeading>13. Contact</SectionHeading>
              <SectionText>For privacy-related questions or requests, contact:</SectionText>
              <div className="mt-2 inline-flex items-center gap-2 bg-blue-50 rounded-lg px-4 py-2.5">
                <Mail size={16} className="text-[#0A66C2]" />
                <a href="mailto:support@evalentx.com" className="text-[#0A66C2] font-semibold hover:underline">support@evalentx.com</a>
              </div>
            </div>
          )}

          {/* ══════════════════════ REFUND POLICY ══════════════════════ */}
          {activeTab === 'refund' && (
            <div data-testid="refund-content">
              <h1 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 mb-6">
                Refund Policy
              </h1>

              <SectionHeading>1. General Principle</SectionHeading>
              <SectionText>
                All payments made on EvalentX are subject to this Refund Policy. Users are advised to carefully review event, competition, assessment, program, or service details before making a payment.
              </SectionText>

              <SectionHeading>2. General Refund Rule</SectionHeading>
              <SectionText>
                Unless otherwise specifically mentioned for a particular program, event, or offering, payments made on EvalentX are generally non-refundable.
              </SectionText>

              <SectionHeading>3. Cases Where Refund May Be Considered</SectionHeading>
              <SectionText>Refunds may be considered in limited cases such as:</SectionText>
              <BulletList items={[
                'Duplicate payment for the same transaction',
                'Payment made but registration not successfully created due to a verified technical error',
                'Event or program cancelled by EvalentX and not rescheduled or substituted',
                'Other exceptional cases approved by EvalentX at its sole discretion',
              ]} />

              <SectionHeading>4. Non-Refundable Situations</SectionHeading>
              <SectionText>Refunds will generally not be provided for:</SectionText>
              <BulletList items={[
                'Change of mind after payment',
                'Inability or unwillingness to participate',
                'Failure to attend or appear',
                'Disqualification due to misconduct, plagiarism, unfair means, or ineligibility',
                'Incorrect details entered at the time of registration',
                'Dissatisfaction unrelated to a verified platform fault',
                'Missed deadlines, missed instructions, or missed communications by the user',
              ]} />

              <SectionHeading>5. Event Hosts, Institutions, and Company-Linked Programs</SectionHeading>
              <SectionText>
                For school, college, company, or event-host linked programs, refunds may be subject to the specific commercial terms agreed for that program. Where an institutional or hosted event has custom rules, those custom rules may apply in addition to this Refund Policy.
              </SectionText>

              <SectionHeading>6. Refund Process</SectionHeading>
              <SectionText>
                To request a refund, the user must contact support@evalentx.com with:
              </SectionText>
              <BulletList items={[
                'Full name',
                'Registered email',
                'Transaction ID',
                'Payment date',
                'Event/program name',
                'Reason for refund request',
              ]} />
              <SectionText>
                EvalentX may request additional supporting information before processing any claim.
              </SectionText>

              <SectionHeading>7. Refund Timeline</SectionHeading>
              <SectionText>
                If a refund is approved, it will generally be processed within 7 to 15 working days, depending on the payment method, gateway, and banking timelines.
              </SectionText>

              <SectionHeading>8. Transaction and Gateway Charges</SectionHeading>
              <SectionText>
                Where applicable, payment gateway charges, transaction fees, convenience charges, or taxes may be deducted from the refundable amount.
              </SectionText>

              <SectionHeading>9. Final Decision</SectionHeading>
              <SectionText>
                EvalentX reserves the right to make the final decision on all refund requests.
              </SectionText>

              <SectionHeading>10. Contact</SectionHeading>
              <SectionText>For refund requests, contact:</SectionText>
              <div className="mt-2 inline-flex items-center gap-2 bg-blue-50 rounded-lg px-4 py-2.5">
                <Mail size={16} className="text-[#0A66C2]" />
                <a href="mailto:support@evalentx.com" className="text-[#0A66C2] font-semibold hover:underline">support@evalentx.com</a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scroll to top */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-10 h-10 rounded-full bg-[#0A66C2] text-white shadow-lg flex items-center justify-center hover:bg-[#004182] transition-colors z-50"
          data-testid="scroll-to-top"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
};

export default TermsPage;
